import { useMemo, useState } from 'react';

import {
    OBJECTIVE_OTHER_SERIES,
    type ObjectiveDefinition,
    type SpendBucketSize,
    type SpendSeries,
} from '@apps/web/components/admin/llm_spend/constants';
import { SpendLineChart } from '@apps/web/components/admin/llm_spend/spend_line_chart';
import {
    formatUsd,
    type ObjectiveBreakdownRow,
    ratio,
    summarizeObjectiveBreakdown,
} from '@apps/web/components/admin/llm_spend/spend_math';
import {
    StackedSpendChart,
    type StackedSpendPoint,
} from '@apps/web/components/admin/llm_spend/stacked_spend_chart';
import {
    Card,
    CardAction,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { type ColumnDef } from '@apps/web/lib/table';

type ObjectiveBucket = {
    readonly bucketStart: string;
    readonly spend: Readonly<Record<string, number>>;
    readonly wakes: Readonly<Record<string, number>>;
    readonly units: number;
};

type ViewMode = 'table' | 'bar' | 'line';

const PERCENT = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
});

function objectiveColumns(
    unitLabel: string,
): Array<ColumnDef<ObjectiveBreakdownRow>> {
    return [
        {
            accessorKey: 'label',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Objective" />
            ),
            cell: ({ row }) => (
                <span className="font-medium">{row.original.label}</span>
            ),
        },
        {
            accessorKey: 'totalSpend',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Total cost" />
            ),
            meta: { align: 'right' },
            cell: ({ row }) => (
                <div className="text-right tabular-nums">
                    {formatUsd(row.original.totalSpend)}
                </div>
            ),
        },
        {
            accessorKey: 'share',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Share" />
            ),
            meta: { align: 'right' },
            cell: ({ row }) => (
                <div className="text-right tabular-nums">
                    {row.original.share === null
                        ? '—'
                        : PERCENT.format(row.original.share)}
                </div>
            ),
        },
        {
            accessorKey: 'wakeCount',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Wakes" />
            ),
            meta: { align: 'right' },
            cell: ({ row }) => (
                <div className="text-right tabular-nums">
                    {row.original.wakeCount.toLocaleString()}
                </div>
            ),
        },
        {
            accessorKey: 'costPerWake',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Cost per wake" />
            ),
            meta: { align: 'right' },
            cell: ({ row }) => (
                <div className="text-right tabular-nums">
                    {formatUsd(row.original.costPerWake)}
                </div>
            ),
        },
        {
            accessorKey: 'costPerUnit',
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title={`Per ${unitLabel}`}
                />
            ),
            meta: { align: 'right' },
            cell: ({ row }) => (
                <div className="text-right tabular-nums">
                    {formatUsd(row.original.costPerUnit)}
                </div>
            ),
        },
    ];
}

export function ObjectiveBreakdown({
    title,
    bucket,
    buckets,
    series,
    totalUnits,
    unitLabel,
}: {
    readonly title: string;
    readonly bucket: SpendBucketSize;
    readonly buckets: ReadonlyArray<ObjectiveBucket>;
    readonly series: ReadonlyArray<ObjectiveDefinition>;
    readonly totalUnits: number;
    readonly unitLabel: string;
}) {
    const [view, setView] = useState<ViewMode>('table');
    const rows = useMemo(
        () => summarizeObjectiveBreakdown(buckets, series, totalUnits),
        [buckets, series, totalUnits],
    );
    const columns = useMemo(() => objectiveColumns(unitLabel), [unitLabel]);
    const chartSeries = useMemo<ReadonlyArray<SpendSeries>>(() => {
        const visibleKeys = new Set(rows.map((row) => row.key));
        return [
            ...series.map((objective, index) => ({
                ...objective,
                color: `var(--spend-series-${(index % 8) + 1})`,
            })),
            {
                ...OBJECTIVE_OTHER_SERIES,
                color: 'var(--spend-series-other)',
            },
        ].filter((objective) => visibleKeys.has(objective.key));
    }, [rows, series]);
    const timePoints = useMemo(() => {
        const knownKeys = new Set(series.map((objective) => objective.key));
        const spendPoints: Array<StackedSpendPoint> = [];
        const costPerUnitPoints: Array<{
            readonly bucketStart: string;
            readonly values: Readonly<Record<string, number | null>>;
        }> = [];

        for (const row of buckets) {
            const values: Record<string, number> = {};
            for (const [key, value] of Object.entries(row.spend)) {
                const chartKey = knownKeys.has(key) ? key : 'other';
                values[chartKey] = (values[chartKey] ?? 0) + value;
            }
            spendPoints.push({ bucketStart: row.bucketStart, values });
            costPerUnitPoints.push({
                bucketStart: row.bucketStart,
                values: Object.fromEntries(
                    Object.entries(values).map(([key, value]) => [
                        key,
                        ratio(value, row.units),
                    ]),
                ),
            });
        }
        return { spendPoints, costPerUnitPoints };
    }, [buckets, series]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">{title}</CardTitle>
                <CardAction>
                    <Select
                        value={view}
                        onValueChange={(value) => setView(value as ViewMode)}
                    >
                        <SelectTrigger size="sm" aria-label="Objective view">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="table">Table</SelectItem>
                            <SelectItem value="bar">
                                Bar chart by time bucket
                            </SelectItem>
                            <SelectItem value="line">
                                Line graph by time bucket
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </CardAction>
            </CardHeader>
            <CardContent>
                {view === 'table' && (
                    <DataTable
                        columns={columns}
                        data={rows}
                        hidePagination={true}
                        initialSorting={[{ id: 'totalSpend', desc: true }]}
                    />
                )}
                {view === 'bar' && (
                    <StackedSpendChart
                        points={timePoints.spendPoints}
                        series={chartSeries}
                        bucket={bucket}
                    />
                )}
                {view === 'line' && (
                    <SpendLineChart
                        points={timePoints.costPerUnitPoints}
                        series={chartSeries}
                        bucket={bucket}
                        valueFormatter={formatUsd}
                    />
                )}
            </CardContent>
        </Card>
    );
}
